import { useState, useCallback } from 'react'
import { Plus, RefreshCw, ShieldCheck, Lock, FileSignature, UserCheck, Upload } from 'lucide-react'
import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  ClassificationTree,
  ClassificationFormDialog,
  ClassificationUploadDialog,
  ClassificationsEmpty,
} from '@/components/classifications'
import {
  useClassificationTree,
  useCreateClassification,
  useUpdateClassification,
  useDeleteClassification,
} from '@/hooks/use-classifications'
import type {
  ClassificationNode,
  CreateClassificationRequest,
  UpdateClassificationRequest,
} from '@/types/classification'

const CATEGORY_CARDS = [
  {
    title: 'Regulatory Compliance',
    description: 'Standards and frameworks mandated by law or industry regulation',
    icon: ShieldCheck,
    tags: ['SOX', 'PCI-DSS', 'FISMA', 'CCPA', 'ITAR', 'EAR', 'CUI'],
  },
  {
    title: 'Intellectual Property',
    description: 'Proprietary assets requiring controlled access',
    icon: Lock,
    tags: ['Trade Secret', 'Patent', 'Copyright', 'Source Code', 'CTI'],
  },
  {
    title: 'Contracts & Agreements',
    description: 'Data governed by contractual or partnership obligations',
    icon: FileSignature,
    tags: ['NDA', 'SLA', 'CRADA', 'Licensing Terms', 'Partner Data'],
  },
  {
    title: 'Privacy & Consent',
    description: 'Data subject to individual privacy rights and consent frameworks',
    icon: UserCheck,
    tags: ['PII', 'HIPAA', 'GDPR', 'Consent Required', 'Data Subject Rights', 'Cross-Border Transfer', 'Retention Limited', 'Purpose Limited'],
  },
] as const

/**
 * Categorization page component
 * Main page for viewing and managing data categorization taxonomy
 */
export function ClassificationsPage() {
  // Dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [editingClassification, setEditingClassification] =
    useState<ClassificationNode | null>(null)
  const [parentClassification, setParentClassification] =
    useState<ClassificationNode | null>(null)
  const [selectedId, setSelectedId] = useState<string | undefined>()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Fetch classification tree
  const { data: tree, isLoading, isError, error, refetch } =
    useClassificationTree()

  // Mutations
  const createClassification = useCreateClassification()
  const updateClassification = useUpdateClassification()
  const deleteClassification = useDeleteClassification()

  // Handlers
  const handleCreateClick = useCallback(() => {
    setEditingClassification(null)
    setParentClassification(null)
    setFormDialogOpen(true)
  }, [])

  const handleEditClick = useCallback((node: ClassificationNode) => {
    setEditingClassification(node)
    setParentClassification(null)
    setFormDialogOpen(true)
  }, [])

  const handleAddChildClick = useCallback((parentNode: ClassificationNode) => {
    setEditingClassification(null)
    setParentClassification(parentNode)
    setFormDialogOpen(true)
  }, [])

  const handleDeleteClick = useCallback(
    (node: ClassificationNode) => {
      const hasChildren = node.children && node.children.length > 0
      if (
        window.confirm(
          `Are you sure you want to delete "${node.displayName}"?${hasChildren ? '\n\nThis classification has child classifications that will also be affected.' : ''}`
        )
      ) {
        deleteClassification.mutate(node.id)
      }
    },
    [deleteClassification]
  )

  const handleSelectClick = useCallback((node: ClassificationNode) => {
    setSelectedId(node.id)
  }, [])

  const handleFormSubmit = useCallback(
    (
      data: CreateClassificationRequest | UpdateClassificationRequest
    ) => {
      if (editingClassification) {
        // Update existing
        updateClassification.mutate(
          {
            id: editingClassification.id,
            data: data as UpdateClassificationRequest,
          },
          {
            onSuccess: () => {
              setFormDialogOpen(false)
              setEditingClassification(null)
            },
          }
        )
      } else {
        // Create new
        createClassification.mutate(data as CreateClassificationRequest, {
          onSuccess: () => {
            setFormDialogOpen(false)
            setParentClassification(null)
          },
        })
      }
    },
    [editingClassification, createClassification, updateClassification]
  )

  const classifications = tree ?? []
  const showEmpty = !isLoading && classifications.length === 0

  return (
    <>
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <h1 className="text-lg font-semibold">Data Categorization</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
            />
            <span className="sr-only">Refresh</span>
          </Button>
          <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            New Category
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4">
        {/* Category Cards Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {CATEGORY_CARDS.map((category) => {
            const isSelected = selectedCategory === category.title
            return (
              <Card
                key={category.title}
                className={`cursor-pointer transition-colors hover:border-primary/50 ${isSelected ? 'border-primary ring-1 ring-primary/20' : ''}`}
                onClick={() => setSelectedCategory(isSelected ? null : category.title)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <category.icon className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{category.title}</CardTitle>
                  </div>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {category.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Error loading classifications:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {showEmpty && <ClassificationsEmpty onCreateClick={handleCreateClick} />}

        {/* Classification Tree */}
        {!isLoading && classifications.length > 0 && (
          <div className="rounded-lg border p-4">
            <ClassificationTree
              nodes={classifications}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onAddChild={handleAddChildClick}
              onSelect={handleSelectClick}
              selectedId={selectedId}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <ClassificationUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
      <ClassificationFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        classification={editingClassification || undefined}
        parentClassification={parentClassification || undefined}
        onSubmit={handleFormSubmit}
        isSubmitting={
          createClassification.isPending || updateClassification.isPending
        }
      />
    </>
  )
}
